import { MediaStats } from '@doxyme/frontend/webrtc/utils';
import { ChangeCounter, IntervalRate, Mode, MinMaxAverage } from './analytics';

type OptionalNumber = number | undefined;

/**
 * Stats get collected every 3 seconds.
 * The value for each property below contains all the values collected to date.
 * These eventually get aggregated into a final value in StatsToLog below.
 */
export type StatsCollectors = {
  metricInboundVideoMos: MinMaxAverage;

  metricInboundAudioMos: MinMaxAverage;

  metricInboundNetworkMos: MinMaxAverage;

  metricInboundNetworkQualityRatio: IntervalRate<'high' | 'medium' | 'low'>;

  metricOutboundNetworkMos: MinMaxAverage;

  metricOutboundNetworkQualityRatio: IntervalRate<'high' | 'medium' | 'low'>;

  metricInboundFrameRate: MinMaxAverage;

  metricOutboundFrameRate: MinMaxAverage;

  metricInboundBitrate: MinMaxAverage;

  metricOutboundBitrate: MinMaxAverage;

  metricInboundVideoBitrate: MinMaxAverage;

  metricOutboundVideoBitrate: MinMaxAverage;

  metricInboundAudioBitrate: MinMaxAverage;

  metricOutboundAudioBitrate: MinMaxAverage;

  metricInboundResolution: IntervalRate<NonNullable<MediaStats['resolution']>>;

  metricInboundResolutionMostUsed: Mode<NonNullable<MediaStats['resolution']>>;

  metricInboundResolutionChangeCount: ChangeCounter<
    NonNullable<MediaStats['resolution']>
  >;

  metricOutboundResolution: IntervalRate<NonNullable<MediaStats['resolution']>>;

  metricOutboundResolutionMostUsed: Mode<NonNullable<MediaStats['resolution']>>;

  metricOutboundResolutionChangeCount: ChangeCounter<
    NonNullable<MediaStats['resolution']>
  >;

  metricCpu: IntervalRate<'nominal' | 'fair' | 'serious' | 'critical'>;
};

export const statsCollectors: StatsCollectors = {
  // The Vonage MOS score. We get an MOS per subscriber so this is based of the values of all subscribers
  metricInboundVideoMos: new MinMaxAverage(),

  // The Vonage MOS score. We get an MOS per subscriber so this is based of the values of all subscribers
  metricInboundAudioMos: new MinMaxAverage(),

  // The inbound network MOS score. Collected in our qualityOfServicePlugin from 3rd party
  // https://www.npmjs.com/package/webrtc-issue-detector lib
  metricInboundNetworkMos: new MinMaxAverage(),

  // The computed NPI based on the inbound MOS score above.
  // The total of all stats below adds up to 1.
  // Multiply each value by 100 to get it as a % if needed.
  metricInboundNetworkQualityRatio: new IntervalRate({
    high: undefined,
    medium: undefined,
    low: undefined,
  }),

  // The outbound network MOS score. Collected in our qualityOfServicePlugin from 3rd party
  // https://www.npmjs.com/package/webrtc-issue-detector lib
  metricOutboundNetworkMos: new MinMaxAverage(),

  // The computed NPI based on the outbound MOS score above.
  // The total of all stats below adds up to 1.
  // Multiply each value by 100 to get it as a % if needed.
  metricOutboundNetworkQualityRatio: new IntervalRate({
    high: undefined,
    medium: undefined,
    low: undefined,
  }),

  // The Vonage frame rate from the web rtc stats report.
  // We get this per subscriber so this is based of the values of all subscribers
  metricInboundFrameRate: new MinMaxAverage(),

  // The Vonage frame rate from the web rtc stats report.
  // We get this from the publisher
  metricOutboundFrameRate: new MinMaxAverage(),

  // The Vonage resolution from the web rtc stats report.
  // We get this per subscriber so this is based of the values of all subscribers
  // The total of all stats below adds up to 1.
  // Multiply each value by 100 to get it as a % if needed.
  metricInboundResolution: new IntervalRate({
    '1080p': undefined,
    '720p': undefined,
    '480p': undefined,
    '360p': undefined,
    '240p': undefined,
    '180p': undefined,
    '144p': undefined,
  }),

  // The inbound resolution which was most frequently in use.
  metricInboundResolutionMostUsed: new Mode(),

  // The number of times the inbound resolution changed.
  metricInboundResolutionChangeCount: new ChangeCounter<
    NonNullable<MediaStats['resolution']>
  >(),

  // The Vonage resolution from the web rtc stats report.
  // We get this from the publisher
  // The total of all stats below adds up to 1.
  // Multiply each value by 100 to get it as a % if needed.
  metricOutboundResolution: new IntervalRate({
    '1080p': undefined,
    '720p': undefined,
    '480p': undefined,
    '360p': undefined,
    '240p': undefined,
    '180p': undefined,
    '144p': undefined,
  }),

  // The outbound resolution which was most frequently in use.
  metricOutboundResolutionMostUsed: new Mode(),

  // The number of times the outbound resolution changed.
  metricOutboundResolutionChangeCount: new ChangeCounter<
    NonNullable<MediaStats['resolution']>
  >(),

  // How long was CPU in each state. The total of all states below adds up to 1.
  // Multiply each value by 100 to get it as a % if needed.
  // We get this from Vonage which uses the browser CPU Pressure API
  metricCpu: new IntervalRate({
    nominal: undefined,
    fair: undefined,
    serious: undefined,
    critical: undefined,
  }),

  // The total inbound bitrate from the web rtc stats report.
  // We get this per subscriber so this is based of the values of all subscribers
  metricInboundBitrate: new MinMaxAverage(),

  // The total outbound bitrate from the web rtc stats report.
  // We get this from the publisher
  metricOutboundBitrate: new MinMaxAverage(),

  // The video bitrate from the web rtc stats report.
  // We get this per subscriber so this is based of the values of all subscribers
  metricInboundVideoBitrate: new MinMaxAverage(),

  // The video bitrate from the web rtc stats report.
  // We get this from the publisher
  metricOutboundVideoBitrate: new MinMaxAverage(),

  // The audio bitrate from the web rtc stats report.
  // We get this per subscriber so this is based of the values of all subscribers
  metricInboundAudioBitrate: new MinMaxAverage(),

  // The audio bitrate from the web rtc stats report.
  // We get this from the publisher
  metricOutboundAudioBitrate: new MinMaxAverage(),
};

/**
 * All the stats we want to log at the end of each session.
 * These values are all driven by the equivalent StatsCollectors above.
 */
export type StatsToLog = {
  // How long was CPU in each state. The total of all states below adds up to 1.
  // Multiply each value by 100 to get it as % if needed.
  metricCpuNominal: OptionalNumber;
  metricCpuFair: OptionalNumber;
  metricCpuCritical: OptionalNumber;
  metricCpuSerious: OptionalNumber;

  // The Vonage MOS score. We get an MOS per subscriber so this is based of the values of all subscribers
  metricInboundVideoMosAvg: OptionalNumber;
  metricInboundVideoMosMin: OptionalNumber;
  metricInboundVideoMosMax: OptionalNumber;

  // The Vonage MOS score. We get an MOS per subscriber so this is based of the values of all subscribers
  metricInboundAudioMosAvg: OptionalNumber;
  metricInboundAudioMosMin: OptionalNumber;
  metricInboundAudioMosMax: OptionalNumber;

  // The inbound network MOS score. Collected in our qualityOfServicePlugin from 3rd party
  // https://www.npmjs.com/package/webrtc-issue-detector lib
  metricInboundNetworkMosAvg: OptionalNumber;
  metricInboundNetworkMosMin: OptionalNumber;
  metricInboundNetworkMosMax: OptionalNumber;

  // The computed NPI based on the inbound MOS score above.
  // The total of all stats below adds up to 1.
  // Multiply each value by 100 to get it as a % if needed.
  metricInboundNetworkQualityRatioHigh: OptionalNumber;
  metricInboundNetworkQualityRatioMedium: OptionalNumber;
  metricInboundNetworkQualityRatioLow: OptionalNumber;

  // The outbound network MOS score. Collected in our qualityOfServicePlugin from 3rd party
  // https://www.npmjs.com/package/webrtc-issue-detector lib
  metricOutboundNetworkMosAvg: OptionalNumber;
  metricOutboundNetworkMosMin: OptionalNumber;
  metricOutboundNetworkMosMax: OptionalNumber;

  // The computed NPI based on the outbound MOS score above.
  // The total of all stats below adds up to 1.
  // Multiply each value by 100 to get it as a % if needed.
  metricOutboundNetworkQualityRatioHigh: OptionalNumber;
  metricOutboundNetworkQualityRatioMedium: OptionalNumber;
  metricOutboundNetworkQualityRatioLow: OptionalNumber;

  // The Vonage frame rate from the web rtc stats report.
  // We get this per subscriber so this is based of the values of all subscribers
  metricInboundFrameRateAvg: OptionalNumber;
  metricInboundFrameRateMin: OptionalNumber;
  metricInboundFrameRateMax: OptionalNumber;

  // The Vonage frame rate from the web rtc stats report.
  // We get this from the publisher
  metricOutboundFrameRateAvg: OptionalNumber;
  metricOutboundFrameRateMin: OptionalNumber;
  metricOutboundFrameRateMax: OptionalNumber;

  // The Vonage resolution from the web rtc stats report.
  // We get this per subscriber so this is based of the values of all subscribers
  // The total of all stats below adds up to 1.
  // Multiply each value by 100 to get it as a % if needed.
  metricInboundResolution1080p: OptionalNumber;
  metricInboundResolution720p: OptionalNumber;
  metricInboundResolution480p: OptionalNumber;
  metricInboundResolution360p: OptionalNumber;
  metricInboundResolution240p: OptionalNumber;
  metricInboundResolution180p: OptionalNumber;
  metricInboundResolution144p: OptionalNumber;

  // The inbound resolution which was most frequently in use.
  metricInboundResolutionMostUsed: MediaStats['resolution'];

  // The number of times the inbound resolution changed.
  metricInboundResolutionChangeCount: OptionalNumber;

  // The Vonage resolution from the web rtc stats report.
  // We get this from the publisher
  // The total of all stats below adds up to 1.
  // Multiply each value by 100 to get it as a % if needed.
  metricOutboundResolution1080p: OptionalNumber;
  metricOutboundResolution720p: OptionalNumber;
  metricOutboundResolution480p: OptionalNumber;
  metricOutboundResolution360p: OptionalNumber;
  metricOutboundResolution240p: OptionalNumber;
  metricOutboundResolution180p: OptionalNumber;
  metricOutboundResolution144p: OptionalNumber;

  // The outbound resolution which was most frequently in use.
  metricOutboundResolutionMostUsed: MediaStats['resolution'];

  // The number of times the outbound resolution changed.
  metricOutboundResolutionChangeCount: OptionalNumber;

  // The total inbound bitrate from the web rtc stats report.
  // We get this per subscriber so this is based of the values of all subscribers
  metricInboundBitrateAvg: OptionalNumber;
  metricInboundBitrateMin: OptionalNumber;
  metricInboundBitrateMax: OptionalNumber;

  // The total bitrate from the web rtc stats report.
  // We get this from the publisher
  metricOutboundBitrateAvg: OptionalNumber;
  metricOutboundBitrateMin: OptionalNumber;
  metricOutboundBitrateMax: OptionalNumber;

  // The video bitrate from the web rtc stats report.
  // We get this per subscriber so this is based of the values of all subscribers
  metricInboundVideoBitrateAvg: OptionalNumber;
  metricInboundVideoBitrateMin: OptionalNumber;
  metricInboundVideoBitrateMax: OptionalNumber;

  // The video bitrate from the web rtc stats report.
  // We get this from the publisher
  metricOutboundVideoBitrateAvg: OptionalNumber;
  metricOutboundVideoBitrateMin: OptionalNumber;
  metricOutboundVideoBitrateMax: OptionalNumber;

  // The audio bitrate from the web rtc stats report.
  // We get this per subscriber so this is based of the values of all subscribers
  metricInboundAudioBitrateAvg: OptionalNumber;
  metricInboundAudioBitrateMin: OptionalNumber;
  metricInboundAudioBitrateMax: OptionalNumber;

  // The audio bitrate from the web rtc stats report.
  // We get this from the publisher
  metricOutboundAudioBitrateAvg: OptionalNumber;
  metricOutboundAudioBitrateMin: OptionalNumber;
  metricOutboundAudioBitrateMax: OptionalNumber;
};
